import { ng } from "@common/ngds";
import { getColumnLength } from "@common/helpers/defaultObject";
import { Drawer, StyleForLine } from "@common/components/Rendering";
import { GraphDirection } from "./SideView";
import { loadcases_getColor } from "../../helpers/Colors";
import {
  load_vertical_point_style,
  load_horizontal_point_style,
  load_horizontal_linear_style,
  load_vertical_linear_style,
  ArrowId,
  max_force_in_m,
  cs_force_text,
} from "../ViewportStyles";
import { drawHorizontalMeassurment, drawVerticalMeassurment } from "./drawMeassurement";

const draw_load_vertical_linear = (
  g: Drawer<ArrowId>,
  hor: number,
  a_vert_start: number,
  a_vert_end: number,
  load_case_color: string,
  displayLoad: boolean,
) => {
  const order = a_vert_start < a_vert_end; // is the start-end in order?
  const vert_start = order ? a_vert_start : a_vert_end;
  const vert_end = order ? a_vert_end : a_vert_start;
  const rough_step = 0.5;

  const dist = vert_end - vert_start;
  const step_count = Math.max(1, Math.round(dist / rough_step));
  const step = dist / step_count;
  for (let step_iter = 1; step_iter <= step_count; step_iter += 1) {
    const vert = vert_start + step_iter * (dist / step_count);
    g.drawLine(
      [hor, vert - step],
      [hor, vert],
      { ...load_vertical_linear_style, lineColor: load_case_color },
      {
        endArrow: "load-arrow",
        display: displayLoad,
      },
    );
  }
};

// todo(Gustav): should we round theese numbers???
const display_string_from_force = (newton: number) => {
  return `${newton / 1000} kN`;
};
const display_string_from_lineload = (newton: number) => {
  return `${newton / 1000} kN/m`;
};
const display_string_long_from_meter = (m: number) => {
  return `${m.toFixed(2)} m`;
};
const display_string_short_from_meter = (m: number) => {
  const round = (x: number) => {
    return x.toFixed();
  };
  return `${round(m * 1000)} mm`;
};

const draw_load_horizontal_linear = (
  g: Drawer<ArrowId>,
  hor: number,
  a_vert_start: number,
  a_vert_end: number,
  a_val_start: number,
  a_val_end: number,
  load_case_color: string,
  val_scale: number,
  display: boolean,
  showMeassure: boolean,
) => {
  const rough_step = 0.6; // [m] how long between the arrows
  const order = a_vert_start < a_vert_end; // is the start-end in order?
  const vert_start = order ? a_vert_start : a_vert_end;
  const vert_end = order ? a_vert_end : a_vert_start;
  const val_start = order ? a_val_start : a_val_end;
  const val_end = order ? a_val_end : a_val_start;

  const style: StyleForLine = { ...load_horizontal_linear_style, lineColor: load_case_color };

  // the load is always drawn on the outer side of the column, so the shaft length
  // is the magnitude of the value; the sign is carried by the arrow direction
  const dis_start = Math.abs(val_start) * val_scale;
  const dis_end = Math.abs(val_end) * val_scale;

  const dist = vert_end - vert_start;
  const step_count = Math.max(1, Math.round(dist / rough_step));
  for (let step_iter = 0; step_iter <= step_count; step_iter += 1) {
    const vert = vert_start + step_iter * (dist / step_count);
    const f = step_iter / step_count;
    const val = val_start + (val_end - val_start) * f; // lerped value at this point
    const tail_hor = hor - Math.abs(val) * val_scale; // far end of the arrow, away from the column
    // A positive value acts along the chosen axis, so the arrow head sits at the
    // column edge. A negative value acts the opposite way, so the head moves to
    // the far end of the very same shaft and points away from the column.
    const is_reversed = val < 0;
    g.drawLine(is_reversed ? [hor, vert] : [tail_hor, vert], is_reversed ? [tail_hor, vert] : [hor, vert], style, {
      endArrow: "load-arrow",
      display,
    });
  }
  g.drawLine([hor, vert_start], [hor, vert_end], style, {
    display,
  });
  g.drawLine([hor - dis_start, vert_start], [hor - dis_end, vert_end], style, { display });

  if (showMeassure) {
    const same = Math.abs(val_start - val_end) < 1;

    if (same) {
      g.drawText(
        [hor, vert_start],
        // the flipped arrows already carry the sign, so the label is the magnitude
        display_string_from_lineload(Math.abs(val_start)),
        { ...cs_force_text, lineColor: load_case_color },
        {
          display: display,
          rotation: "upward",
          horiAlign: "left",
          vertOffset: -8,
          horiOffset: -3,
          id: "lineload_upward_left",
        },
      );
    } else {
      g.drawText(
        [hor, vert_start],
        display_string_from_lineload(Math.abs(val_start)),
        { ...cs_force_text, lineColor: load_case_color },
        {
          display: display,
          rotation: "upward",
          horiAlign: "right",
          vertOffset: 8,
          horiOffset: -3,
          id: "lineload_upward_right",
        },
      );

      g.drawText(
        [hor, vert_start + dist],
        display_string_from_lineload(Math.abs(val_end)),
        { ...cs_force_text, lineColor: load_case_color },
        {
          display: display,
          rotation: "upward",
          horiAlign: "left",
          vertOffset: -8,
          horiOffset: -3,
          id: "lineload_upward_left",
        },
      );
    }

    // Note: the position/depth dimensions are NOT drawn per load here — they are
    // drawn once per lane as a single chain across all line loads (see below),
    // so only the first load contributes 0 -> xs and only the last xe -> L.
  }
};

export const get_loads = (selProject: ng.frontend.column.Project | null) => {
  const input = selProject?.data?.input;
  const structure = input?.structures?.[0];
  const element = structure?.elements?.[0] || null;
  const loading = structure?.loading || null;
  const loads = loading?.loads ?? [];
  const loadCombinations = loading?.combinations ?? [];

  const elementLength = element && input ? getColumnLength(element, input) : 0;
  const loadCases = loading?.loadcases ?? [];

  const vertical: ng.load.Data[] = [];
  const horizontal: ng.load.Data[] = [];
  const lines: ng.load.Data[] = [];

  for (const load of loads) {
    if (load.type !== ng.load.Type.TYPE_FORCE) continue;

    if (load.distribution === ng.load.DistributionType.DISTRIBUTION_TYPE_POINT) {
      const isVertical =
        Math.abs(load.direction?.x ?? 0) === 1 && (load.direction?.y ?? 0) === 0 && (load.direction?.z ?? 0) === 0;
      if (isVertical) {
        vertical.push(load);
      } else {
        // Horizontal point loads act in global +Y or +Z with no X component.
        const isHorizontal = load.direction?.x === 0 && (load.direction?.y != 0 || load.direction?.z != 0);
        if (isHorizontal) {
          horizontal.push(load);
        }
      }
    } else if (load.distribution === ng.load.DistributionType.DISTRIBUTION_TYPE_LINE) {
      lines.push(load);
    }
  }

  return {
    vertical,
    horizontal,
    line: lines,
    elementLength: elementLength,
    loadCases,
    loadCombinations,
  };
};

export const getSelectedLoadcaseGuids = (
  project: ng.frontend.column.Project,
  selectedLcombGuid: string,
): Set<string> => {
  const structure = project?.data?.input?.structures?.[0];
  const loadcases = structure?.loading?.loadcases ?? [];
  const combinations = structure?.loading?.combinations ?? [];
  const selectedGuid = selectedLcombGuid?.replace("envelope_", "");

  if (!selectedGuid || selectedGuid === "-1" || selectedGuid === "envelope") {
    return new Set<string>();
  }

  const selectedLoadcase = loadcases.find((lcase) => lcase.id?.guid === selectedGuid);
  if (selectedLoadcase?.id?.guid) {
    return new Set<string>([selectedLoadcase.id.guid]);
  }

  const selectedCombination = combinations.find((comb) => comb.id?.guid === selectedGuid);
  const lcaseGuids = selectedCombination?.parts
    ?.map((part) => part.lcaseGuid)
    .filter((guid): guid is string => Boolean(guid));

  return new Set<string>(lcaseGuids ?? []);
};

export const classify_direction = (dir: ng.geometry.Vector3D): "x" | "y" | "z" => {
  // todo(Gustav): currently this assumes the axes are normalized (and they probably should be)
  // but perhaps we should dot compare with all relevant axes and pick the axis that it's most close to?
  if (Math.abs(dir.y) === 1) return "y";
  if (Math.abs(dir.z) === 1) return "z";
  return "x";
};

export const force_from_point_load = (load: ng.load.Data): number => load.values?.[0];

export const drawLoads = (
  g: Drawer<ArrowId>,
  props: {
    data: ng.frontend.column.Project;
    viewDirection: GraphDirection;
    center_line_hor: number;
    column_size: number;
    display: boolean;
    selectedLcombGuid: string;
    showMeassure: boolean;
    column_height: number;
  },
) => {
  const { vertical: verticalPointLoads, horizontal: horizontalPointLoads, line: lineLoads } = get_loads(props.data);
  const selectedLoadcaseGuids = getSelectedLoadcaseGuids(props.data, props.selectedLcombGuid);

  const isLoadInSelectedCombination = (load: ng.load.Data): boolean => {
    if (!load.lcaseGuid) {
      return false;
    }
    return selectedLoadcaseGuids.has(load.lcaseGuid);
  };

  const { column_size, center_line_hor } = props;

  const is_direction_visible = (dir: ng.geometry.Vector3D) => {
    const dc = classify_direction(dir);
    switch (props.viewDirection) {
      case "xy":
        return dc !== "z";
      case "xz":
        return dc !== "y";
    }
  };

  // line loads
  const all_liho_loads = new Array<{
    lcaseGuid: string | undefined;
    load_case_color: string;
    start: number;
    end: number;
    start_value: number;
    end_value: number;
    is_visible: boolean;
  }>();
  for (const lilo of lineLoads) {
    if (!lilo.direction) continue;
    const dir_vis = is_direction_visible(lilo.direction);
    const load_vis = isLoadInSelectedCombination(lilo);
    const is_visible = dir_vis && load_vis && props.display;

    const start = lilo.positions[0].x;
    const end = lilo.positions[1].x;
    const start_value = lilo.values[0];
    const end_value = lilo.values[1];
    const load_case_color = loadcases_getColor(props.data, lilo.lcaseGuid).toHex();
    const direction = classify_direction(lilo.direction);

    if (direction == "x") {
      // for now we only have self weight as x-directed line load so we make it visible
      const visible = true; //is_visible
      // todo(Gustav): what's the position?
      const self_weight_hor = center_line_hor;
      // todo(Gustav): how to draw the force?
      draw_load_vertical_linear(g, self_weight_hor, -start, -end, load_case_color, visible);
    } else {
      all_liho_loads.push({
        lcaseGuid: lilo.lcaseGuid,
        start,
        end,
        start_value,
        end_value,
        load_case_color,
        is_visible,
      });
    }
  }

  const linear_offset_hor_initial = 0.5;
  const linear_offset_hor_spacing = 0.2;

  const force_scale = (() => {
    const lin_ho = all_liho_loads.flatMap((x) => [x.start_value, x.end_value]);
    const po_hor = horizontalPointLoads.map(force_from_point_load);
    const po_ver = verticalPointLoads.map(force_from_point_load);

    const calc_load_scale = (forces: number[], max_dist: number) => {
      // given a list of forces and a max distance this function will return
      // a scale such that scale*|force| will be between 0 and max_dist
      const valid_forces = forces.filter(Number.isFinite); // exclude nan and inf
      const max_force = valid_forces.length > 0 ? Math.max(...valid_forces.map((f) => Math.abs(f))) : 0;
      const scale = max_force > 0 ? max_dist / max_force : 0;
      return scale;
    };
    const hor = calc_load_scale(lin_ho.concat(po_hor), max_force_in_m);
    const ver = calc_load_scale(po_ver, max_force_in_m);

    return { hor, ver };
  })();

  // Group the horizontal loads (line + point) by load case. Every load that
  // belongs to the same load case is drawn in the same "lane", i.e. at the same
  // horizontal offset from the column edge, so the line load and the point load
  // of a given case line up. Consecutive load cases are pushed further out using
  // linear_offset_hor_spacing so they don't overlap.
  type LaneLineLoad = {
    start: number;
    end: number;
    start_value: number;
    end_value: number;
    is_visible: boolean;
  };
  type LanePointLoad = {
    vert: number;
    force: number;
    is_visible: boolean;
  };
  type Lane = {
    color: string;
    lineLoads: LaneLineLoad[];
    pointLoads: LanePointLoad[];
  };

  const lanes = new Map<string, Lane>();
  const getLane = (lcaseGuid: string | undefined, color: string): Lane => {
    const key = lcaseGuid ?? "__no_lcase__";
    let lane = lanes.get(key);
    if (!lane) {
      lane = { color, lineLoads: [], pointLoads: [] };
      lanes.set(key, lane);
    }
    return lane;
  };

  for (const load of all_liho_loads) {
    getLane(load.lcaseGuid, load.load_case_color).lineLoads.push({
      start: load.start,
      end: load.end,
      start_value: load.start_value,
      end_value: load.end_value,
      is_visible: load.is_visible,
    });
  }

  for (const hopolo of horizontalPointLoads) {
    const dir_vis = hopolo.direction ? is_direction_visible(hopolo.direction) : false;
    const load_vis = isLoadInSelectedCombination(hopolo);
    const is_visible = Boolean(dir_vis) && load_vis && props.display;

    const pos = hopolo.positions?.[0];
    const vert = -(pos?.x ?? 0);
    const force = force_from_point_load(hopolo);
    if (!Number.isFinite(vert) || !Number.isFinite(force)) continue;

    const color = loadcases_getColor(props.data, hopolo.lcaseGuid).toHex();
    getLane(hopolo.lcaseGuid, color).pointLoads.push({ vert, force, is_visible });
  }

  {
    let linear_offset_hor = linear_offset_hor_initial;
    for (const lane of lanes.values()) {
      const base_hor = center_line_hor - column_size / 2 - linear_offset_hor;

      // horizontal line loads for this load case
      for (const load of lane.lineLoads) {
        draw_load_horizontal_linear(
          g,
          base_hor,
          -load.start,
          -load.end,
          load.start_value,
          load.end_value,
          lane.color,
          force_scale.hor,
          load.is_visible,
          props.showMeassure,
        );
      }

      // Chained depth measurement for ALL line loads in this lane, spanning the
      // full column height as one chain: 0 -> xs1 -> xe1 -> xs2 -> xe2 -> ... -> L.
      // Because it is built from the sorted set of boundaries {0, L, every load's
      // xs and xe}, only the first load yields the 0 -> xs segment and only the
      // last the xe -> L segment, and the gaps between consecutive loads are
      // measured too. The whole chain sits on one vertical line to the left of
      // every line-load arrow in the lane.
      if (props.showMeassure) {
        const visibleLines = lane.lineLoads.filter((load) => load.is_visible);
        if (visibleLines.length > 0) {
          const line_max_extent = visibleLines.reduce(
            (m, load) => Math.max(m, Math.max(Math.abs(load.start_value), Math.abs(load.end_value)) * force_scale.hor),
            0,
          );
          const chain_hor = base_hor - line_max_extent;
          // sorted, de-duplicated boundaries along the column axis
          const breaks = Array.from(
            new Set<number>([0, props.column_height, ...visibleLines.flatMap((load) => [load.start, load.end])]),
          ).sort((a, b) => a - b);
          for (let i = 0; i + 1 < breaks.length; i += 1) {
            const x0 = breaks[i];
            const x1 = breaks[i + 1];
            const seg = x1 - x0;
            if (seg <= 0) continue;
            drawVerticalMeassurment(g, {
              end_hor: chain_hor,
              start_vert: -x0,
              end_vert: -x1,
              text: display_string_long_from_meter(seg),
              is_visible: true,
            });
          }
        }
      }

      // horizontal point loads for this load case (arrow head sits at base_hor,
      // i.e. the same horizontal position as the line load above)
      let index = 0;
      for (const load of lane.pointLoads) {
        const length = (Math.abs(load.force) * force_scale.hor) / 2;
        const tail_hor = base_hor - length; // far end of the arrow, away from the column
        // A positive load acts along the chosen axis, so the arrow head sits at the
        // column edge. A negative load acts the opposite way, so the head moves to
        // the far end of the very same shaft and points away from the column.
        const is_reversed = load.force < 0;
        g.drawLine(
          is_reversed ? [base_hor, load.vert] : [tail_hor, load.vert],
          is_reversed ? [tail_hor, load.vert] : [base_hor, load.vert],
          { ...load_horizontal_point_style, lineColor: lane.color, thickness: 1.2 },
          {
            endArrow: "load-arrow",
            display: load.is_visible,
          },
        );
        if (props.showMeassure) {
          g.drawText(
            [tail_hor, load.vert],
            // the flipped arrow already carries the sign, so the label is the magnitude
            display_string_from_force(Math.abs(load.force)),
            { ...cs_force_text, lineColor: lane.color },
            {
              display: load.is_visible,
              horiAlign: "center",
              vertAlign: "bottom",
              vertOffset: 0,
              horiOffset: -5,
              rotation: "upward",
              id: "pointload_center_bottom_" + index,
            },
          );
        }
        index++;
      }

      // Chained depth measurements for the point loads in this lane. Measuring
      // every load from the top (0) makes the dimensions overlap when several
      // loads share a lane, so instead each load is measured from the previous
      // (shallower) one — e.g. loads at x=2 and x=3 read as 2 then 1. The whole
      // chain is drawn on one vertical line to the left of every point arrow.
      if (props.showMeassure) {
        const chained = lane.pointLoads
          .filter((load) => load.is_visible)
          // from the column top downwards (x ascending == vert descending)
          .sort((a, b) => b.vert - a.vert);
        if (chained.length > 0) {
          const max_point_length = chained.reduce(
            (m, load) => Math.max(m, (Math.abs(load.force) * force_scale.hor) / 2),
            0,
          );
          const chain_hor = base_hor - max_point_length;
          let prev_vert = 0;
          for (const load of chained) {
            drawVerticalMeassurment(g, {
              end_hor: chain_hor,
              start_vert: prev_vert,
              end_vert: load.vert,
              text: display_string_long_from_meter(prev_vert - load.vert),
              is_visible: load.is_visible,
            });
            prev_vert = load.vert;
          }
        }
      }

      // advance to the next lane by the widest load in this lane
      let lane_extent = 0;
      for (const load of lane.lineLoads) {
        lane_extent = Math.max(
          lane_extent,
          force_scale.hor * Math.max(Math.abs(load.start_value), Math.abs(load.end_value)),
        );
      }
      for (const load of lane.pointLoads) {
        lane_extent = Math.max(lane_extent, (force_scale.hor * Math.abs(load.force)) / 2);
      }
      linear_offset_hor += lane_extent + linear_offset_hor_spacing;
    }
  }

  // vertical point loads — grouped by axial position (x). Loads that share the
  // same x are stacked on top of each other and scaled together so the total
  // height of a stack never exceeds max_force_in_m ("the height given"). The
  // whole stack is aligned on the column center line.
  {
    type VertStackLoad = {
      vert: number;
      offset: number;
      force: number;
      color: string;
      is_visible: boolean;
    };

    const vert_stacks = new Map<string, VertStackLoad[]>();
    for (const vepolo of verticalPointLoads) {
      const pos = vepolo.positions?.[0];
      const vepolo_vert = pos?.x ?? 0;
      const force = force_from_point_load(vepolo);
      if (!Number.isFinite(vepolo_vert) || !Number.isFinite(force)) continue;

      const load_vis = isLoadInSelectedCombination(vepolo);
      const is_visible = load_vis && props.display;
      const color = loadcases_getColor(props.data, vepolo.lcaseGuid).toHex();
      const offset = (props.viewDirection === "xy" ? pos?.y : pos?.z) ?? 0;

      const key = vepolo_vert.toFixed(6);
      let stack = vert_stacks.get(key);
      if (!stack) {
        stack = [];
        vert_stacks.set(key, stack);
      }
      stack.push({ vert: vepolo_vert, offset, force, color, is_visible });
    }

    // One shared scale so the tallest stack (sum of its forces) exactly fills the
    // available height and no stack ever exceeds it.
    const stack_sum = (stack: VertStackLoad[]) => stack.reduce((acc, l) => acc + Math.max(0, l.force), 0);
    const max_stack_sum = Math.max(0, ...[...vert_stacks.values()].map(stack_sum));
    const vert_stack_scale = max_stack_sum > 0 ? max_force_in_m / max_stack_sum : 0;

    for (const stack of vert_stacks.values()) {
      // the bottom of the stack sits at the load point (-vert); each subsequent
      // load is drawn on top of the previous one. The vertical position is set
      // purely by the stacking; an eccentric load only shifts horizontally.
      let seg_bottom = -stack[0].vert;
      let index = 0;
      for (const load of stack) {
        const length = load.force * vert_stack_scale;
        const seg_top = seg_bottom - length; // further from the column (upwards)
        const load_hor = center_line_hor + load.offset; // eccentricity shifts horizontally only

        g.drawLine(
          [load_hor, seg_top],
          [load_hor, seg_bottom],
          { ...load_vertical_point_style, lineColor: load.color },
          {
            endArrow: "load-arrow",
            display: load.is_visible,
          },
        );
        if (props.showMeassure) {
          const is_offset_visible = load.offset != 0;
          g.drawText(
            [load_hor + (load.offset <= 0 ? 0 : 0.3), seg_bottom - length * (is_offset_visible ? 2 / 3 : 1 / 2)],
            display_string_from_force(load.force),
            { ...cs_force_text, lineColor: load.color },
            {
              display: load.is_visible,
              horiAlign: "right",
              horiOffset: -5,
              id: "vertical-load-text_" + index,
            },
          );

          if (is_offset_visible) {
            drawHorizontalMeassurment(g, {
              start_hor: load_hor,
              end_hor: center_line_hor,
              end_vert: seg_bottom - length - 0.1,
              text: Math.abs(Math.round(load.offset * 1000)), //display_string_short_from_meter(Math.abs(load.offset)),
              is_visible: load.is_visible,
              textHoriOffset: -load.offset * 1000 * 0.1 + (load.offset < 0 ? 10 : -10), // nudge the eccentricity distance text to the right
            });
          }
        }
        seg_bottom = seg_top;
        index++;
      }
    }
  }
};
